import {
  Header,
  MenuList,
  MenuListItem,
  Space,
  Spacer,
  StatusBadge,
  StatusBadgeStatuses,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { FieldGridStyled } from './styles/FieldGridStyled'
import { FieldLabelStyled } from './styles/FieldLabelStyled'
import { FieldStyled } from './styles/FieldStyled'
import { FieldValueStyled } from './styles/FieldValueStyled'
import { NoneTextStyled } from './styles/NoneTextStyled'

export interface ILegalEntityDetailsViewProps {
  name: string
  isActive: boolean
  baseCurrencyName: string
  externalId: string
  country: string
  onEdit: () => void
  onDisable: () => void
  isDisableDisabled?: boolean
  onReactivate: () => void
}

export const LegalEntityDetailsView = ({
  name,
  isActive,
  baseCurrencyName,
  externalId,
  country,
  onEdit,
  onDisable,
  isDisableDisabled = false,
  onReactivate,
}: ILegalEntityDetailsViewProps) => {
  const intl = useProcurifyIntl()

  const noneAdded = (
    <NoneTextStyled>{intl.formatMessage(messages.noneAdded)}</NoneTextStyled>
  )

  return (
    <>
      <Space direction='horizontal' justify='space-between' align='center'>
        <Space direction='horizontal' align='center'>
          <Header as='h2'>{name}</Header>
          <StatusBadge
            type={
              isActive
                ? StatusBadgeStatuses.SUCCESS
                : StatusBadgeStatuses.INACTIVE
            }
            text={intl.formatMessage(
              isActive ? messages.statusActive : messages.statusInactive
            )}
          />
        </Space>

        {/* Default trigger is the three-dot (⋯) icon button, matching Figma. */}
        <MenuList anchorOrigin={{ vertical: 'center', horizontal: 'center' }}>
          <MenuListItem
            icon='edit'
            text={intl.formatMessage(messages.actionEdit)}
            onClick={onEdit}
          />
          {isActive ? (
            <MenuListItem
              icon='disable'
              text={intl.formatMessage(messages.actionDisable)}
              disabled={isDisableDisabled}
              tooltip={
                isDisableDisabled
                  ? intl.formatMessage(messages.lastActiveEntityTooltip)
                  : undefined
              }
              onClick={() => {
                if (isDisableDisabled) return
                onDisable()
              }}
            />
          ) : (
            <MenuListItem
              icon='rotate'
              text={intl.formatMessage(messages.actionEnable)}
              onClick={onReactivate}
            />
          )}
        </MenuList>
      </Space>

      <Spacer space={1.5} />

      {/* Base currency / External ID on row 1, Country on row 2 — see Figma. */}
      <FieldGridStyled>
        <FieldStyled>
          <FieldLabelStyled>
            {intl.formatMessage(messages.fieldBaseCurrency)}
          </FieldLabelStyled>
          <FieldValueStyled>{baseCurrencyName || noneAdded}</FieldValueStyled>
        </FieldStyled>
        <FieldStyled>
          <FieldLabelStyled>
            {intl.formatMessage(messages.fieldExternalId)}
          </FieldLabelStyled>
          <FieldValueStyled>{externalId || noneAdded}</FieldValueStyled>
        </FieldStyled>
        <FieldStyled>
          <FieldLabelStyled>
            {intl.formatMessage(messages.fieldCountry)}
          </FieldLabelStyled>
          <FieldValueStyled>{country || noneAdded}</FieldValueStyled>
        </FieldStyled>
      </FieldGridStyled>
    </>
  )
}

LegalEntityDetailsView.displayName = 'LegalEntityDetailsView'
